import React, { useState } from 'react';
import { Loader2Icon, TriangleAlert } from 'lucide-react';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/toast/toast';
import notifyHB from '@/config/notifyHB';
import { isHttpError } from '@/http';
import { abandonOnboarding } from '@/use-cases/abandon-onboarding';
import { isOnboardingConflict } from '@/utils/is-onboarding-conflict';
import { clearOnboardingStorage } from '../request-signature/onboarding-resume';
import { SUPPORT_EMAIL } from '../request-signature/onboarding-in-review';
import styles from './cancel-onboarding-dialog.module.scss';

interface CancelOnboardingDialogProps {
	onboardingUlid: string;
	// Re-reads the status so the card follows what the backend now reports.
	onSettled: () => Promise<unknown>;
}

const CancelOnboardingDialog: React.FC<CancelOnboardingDialogProps> = ({
	onboardingUlid,
	onSettled,
}) => {
	const [open, setOpen] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);

	const cancelRequest = async () => {
		setIsCancelling(true);
		try {
			await abandonOnboarding(onboardingUlid);
			clearOnboardingStorage();
			setOpen(false);
			await onSettled();
			toast({
				mode: 'default',
				description: 'Cancelamos tu solicitud.',
			});
		} catch (error) {
			setOpen(false);
			// The backend refused: paid, in review, a first onboarding (409) or
			// no longer the active process (404).
			if (
				isOnboardingConflict(error) ||
				(isHttpError(error) && error.status === 404)
			) {
				await onSettled();
				toast({
					mode: 'caution',
					description: `No es posible cancelar esta solicitud desde aquí. Escríbenos a ${SUPPORT_EMAIL}.`,
				});
			} else {
				notifyHB('dashboard', 'CancelOnboardingDialog', error);
				toast({
					mode: 'destructive',
					description:
						'No pudimos cancelar tu solicitud. Intenta de nuevo más tarde.',
				});
			}
		} finally {
			setIsCancelling(false);
		}
	};

	return (
		<AlertDialog
			open={open}
			onOpenChange={(next) => !isCancelling && setOpen(next)}
		>
			<AlertDialogTrigger asChild>
				<Button variant="ghost">Cancelar solicitud</Button>
			</AlertDialogTrigger>
			<AlertDialogContent className={styles.content}>
				<AlertDialogHeader className={styles.header}>
					<span className={styles.icon} aria-hidden="true">
						<TriangleAlert size={20} />
					</span>
					<div>
						<AlertDialogTitle className={styles.title}>
							¿Cancelar esta solicitud?
						</AlertDialogTitle>
						<AlertDialogDescription className={styles.description}>
							Se eliminará el progreso de esta solicitud. Podrás
							iniciar una nueva cuando quieras.
						</AlertDialogDescription>
					</div>
				</AlertDialogHeader>
				<AlertDialogFooter className={styles.footer}>
					<Button
						variant="outline"
						disabled={isCancelling}
						onClick={() => setOpen(false)}
					>
						Volver
					</Button>
					<Button
						variant="destructive"
						disabled={isCancelling}
						onClick={cancelRequest}
					>
						{isCancelling && (
							<Loader2Icon className="animate-spin" />
						)}
						Cancelar solicitud
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
};

export default CancelOnboardingDialog;
